import styled from 'styled-components'
import {
  FONT_STACK,
  financeControlSkin,
  sureco,
} from '../shared/finance.styles'
import type { RoleAccessTone } from './userDisplay'

export const PageContent = styled.div<{ $hasFloatingBar?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  color: ${sureco.fg1};
  font-family: ${FONT_STACK};
  /* Clearance for what floats over the page: the support chat button in the
     corner, and the bulk bar when there is a selection. Without it they land on
     the pagination and the last rows. */
  padding-bottom: ${({ $hasFloatingBar }) =>
    $hasFloatingBar ? '128px' : '80px'};

  ${financeControlSkin}
`

export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

export const SearchInputWrap = styled.div`
  flex: 0 1 320px;

  .ant-input-affix-wrapper {
    height: 34px;
    padding: 0 12px;
  }
`

/**
 * Clears every filter at once. Reads as a link rather than a button so it stays
 * subordinate to the controls it resets.
 */
export const ResetLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${sureco.fg1};
  cursor: pointer;

  &:disabled {
    color: ${sureco.placeholder};
    cursor: default;
  }
  &:not(:disabled):hover {
    color: ${sureco.teal};
  }
  &:focus-visible {
    outline: 2px solid ${sureco.teal};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

/** Single row action: a 24px icon target that keeps rows on the 42px pitch. */
export const KebabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  appearance: none;
  border: none;
  border-radius: 6px;
  background: none;
  color: ${sureco.fg1};
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background: ${sureco.offWhite};
  }
  &:focus-visible {
    outline: 2px solid ${sureco.teal};
    outline-offset: 1px;
  }
`

export const ShowingBar = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: ${sureco.fg3};
`

/**
 * The leftover-access count, toned and clickable, in the line that already says
 * what the table is showing. Someone is always stranded, so this is a standing
 * condition rather than an event: a banner would be an alarm that can never be
 * satisfied, and it would push the table down on every load. Shares the cell's
 * color so the count and the rows it narrows to read as one signal.
 */
export const NoCompanyLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${sureco.danger};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.75;
  }
  &:focus-visible {
    outline: 2px solid ${sureco.teal};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const TableCard = styled.div`
  background: ${sureco.white};
  border: 1px solid ${sureco.border};
  border-radius: 12px;
  box-shadow: ${sureco.shadowSm};
  overflow: hidden;

  /* Both tables are wider than the viewport, and macOS hides its overlay
     scrollbar until you scroll, so nothing says the cut-off column can be
     reached. An explicit height keeps the bar on screen. */
  .ant-table-body {
    scrollbar-width: thin;
    scrollbar-color: ${sureco.borderStrong} transparent;
  }

  .ant-table-body::-webkit-scrollbar {
    height: 9px;
  }

  .ant-table-body::-webkit-scrollbar-thumb {
    background: ${sureco.borderStrong};
    border-radius: 99px;
  }

  /* Header row — tinted fill, sentence case, same 14px scale as the body.
     Tripled to outrank antd's own \`.ant-table-small\` density rules. */
  &&& .ant-table-thead > tr > th {
    background: ${sureco.offWhite};
    color: ${sureco.fg1};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    height: 48px;
    border-bottom: 1px solid ${sureco.border};
    padding: 0 8px 0 12px;
  }

  /* Keep antd's column separator but draw it as the design's short divider. */
  .ant-table-thead > tr > th:not(:last-child)::before {
    background-color: ${sureco.borderStrong} !important;
    width: 2px !important;
    height: 14px !important;
  }

  /* Keep the sort caret next to its label. antd stretches the column title to
     fill the cell when the column is also ellipsized, which pushed the Email
     caret to the far edge and made it read as the next column's control. */
  .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 6px;
  }
  .ant-table-column-title {
    flex: 0 1 auto;
  }

  /* One line per header. A wrapped label would deepen the header row and, on
     the date columns, read as two separate headings. */
  &&& .ant-table-thead > tr > th .ant-table-column-title,
  &&& .ant-table-thead > tr > th {
    white-space: nowrap;
  }
  .ant-table-column-sorter {
    margin-inline-start: 0;
  }

  /* Body cells. The row height is fixed rather than derived from padding so a
     22px pill or a 24px icon button doesn't push the row off the design's 42px
     pitch; content taller than that (wrapped tags) still grows the row. */
  &&& .ant-table-tbody > tr > td {
    font-size: 14px;
    line-height: 18px;
    color: ${sureco.fg1};
    border-bottom: 1px solid ${sureco.border};
    height: 42px;
    padding: 0 12px;
  }
  .ant-table-tbody > tr:hover > td {
    background: ${sureco.offWhite};
  }

  /* The whole row opens its history, so it reads as one target. */
  .ant-table-tbody > tr.ant-table-row {
    cursor: pointer;
  }

  /* A tint rather than antd's fill, so a selected row stays readable. Opaque
     rather than an alpha of the teal: the frozen Email column sits over the
     scrolling ones, and a translucent fill shows them through it. */
  &&& .ant-table-tbody > tr.ant-table-row-selected > td {
    background: #e6f6f6;
  }
  &&& .ant-table-tbody > tr.ant-table-row-selected:hover > td {
    background: #d6f0f1;
  }

  /* Pager on the left, page size on the right, as in the design. */
  .ant-table-pagination.ant-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 14px 20px;
    margin: 0 !important;
    border-top: 1px solid ${sureco.border};
    background: ${sureco.white};
    border-radius: 0 0 12px 12px;
    gap: 6px;
  }

  /* Outranks antd's \`.ant-pagination-mini .ant-pagination-options\` margin. */
  &&& .ant-pagination-options {
    margin-inline-start: auto;
  }

  &&& .ant-pagination-options .ant-select {
    width: 240px;
    height: 36px;
  }
  &&& .ant-pagination-options .ant-select-selector {
    height: 36px;
    border-radius: 6px;
    border-color: ${sureco.borderStrong};
  }
  .ant-pagination-options .ant-select-selection-item {
    line-height: 34px;
    font-size: 14px;
    color: ${sureco.fg1};
  }

  &&& .ant-pagination-item {
    border-radius: 6px;
    border: 1px solid ${sureco.border};
    background: ${sureco.white};
    min-width: 32px;
    height: 32px;
    line-height: 30px;
    margin: 0;
    transition: all 0.15s;
  }

  .ant-pagination-item a {
    font-size: 14px;
    font-weight: 500;
    color: ${sureco.fg1};
  }

  &&& .ant-pagination-item-active {
    border-color: ${sureco.teal};
    background: ${sureco.teal};
  }

  &&& .ant-pagination-item-active a {
    color: ${sureco.onTeal};
    font-weight: 600;
  }

  .ant-pagination-item:not(.ant-pagination-item-active):hover {
    border-color: ${sureco.teal} !important;
  }

  .ant-pagination-item:not(.ant-pagination-item-active):hover a {
    color: ${sureco.teal} !important;
  }

  &&& .ant-pagination-prev,
  &&& .ant-pagination-next {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
    border-radius: 6px;
    margin: 0;
  }

  &&& .ant-pagination-prev .ant-pagination-item-link,
  &&& .ant-pagination-next .ant-pagination-item-link {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid ${sureco.border};
    background: ${sureco.white};
    color: ${sureco.fg2};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
  }

  .ant-pagination-prev:hover .ant-pagination-item-link,
  .ant-pagination-next:hover .ant-pagination-item-link {
    border-color: ${sureco.teal} !important;
    color: ${sureco.teal} !important;
  }

  .ant-pagination-disabled .ant-pagination-item-link {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

/**
 * Status badges, matched to the design's palette. Only the in-flight state
 * ("Pending registration") is uncolored, and it takes the squarer 10px radius;
 * every settled state carries a status family at full radius.
 */
const STATUS_PILL_COLORS = {
  active: {
    bg: sureco.successLight,
    border: sureco.successBorder,
    fg: sureco.successText,
    radius: '9999px',
  },
  invited: {
    bg: sureco.white,
    border: sureco.border,
    fg: sureco.fg3,
    radius: '10px',
  },
  expired: {
    bg: sureco.errorLight,
    border: sureco.errorBorder,
    fg: sureco.errorText,
    radius: '9999px',
  },
  none: {
    bg: sureco.noticeLight,
    border: sureco.noticeBorder,
    fg: sureco.noticeText,
    radius: '9999px',
  },
} as const

export const StatusPill = styled.span<{
  $variant: keyof typeof STATUS_PILL_COLORS
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  background: ${({ $variant }) => STATUS_PILL_COLORS[$variant].bg};
  border: 1px solid ${({ $variant }) => STATUS_PILL_COLORS[$variant].border};
  border-radius: ${({ $variant }) => STATUS_PILL_COLORS[$variant].radius};
  color: ${({ $variant }) => STATUS_PILL_COLORS[$variant].fg};
`

export const CellText = styled.span`
  font-size: 14px;
  color: ${sureco.fg1};
`

/**
 * The Companies cell with nothing in it. Toned rather than a dash: every such
 * row holds the role with nothing left to invoice, which is the state the
 * No companies filter collects.
 */
export const NoCompaniesText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${sureco.danger};
`

/**
 * Clips its own text rather than relying on the column's ellipsis: the cell is
 * frozen, so anything that overflows is painted over the scrolling columns.
 */
export const EmailCell = styled.span`
  display: block;
  max-width: 100%;
  color: ${sureco.fg1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

/** Actions sit opposite the filters, matching the Company tab's toolbar. */
export const SearchRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`

export const FilterField = styled.div<{ $wide?: boolean }>`
  width: ${({ $wide }) => ($wide ? '240px' : '200px')};

  .ant-select-selector {
    height: 34px !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: 32px !important;
    font-size: 14px;
  }
`

/** Column-header filter panel (the funnel icon's dropdown). */
export const HeaderFilterPanel = styled.div`
  padding: 8px;
  min-width: 260px;
`

/** Narrower panel for the two-option Yes/No column filters. */
export const CompactFilterPanel = styled(HeaderFilterPanel)`
  min-width: 140px;
`

/**
 * Caps the checkbox list at roughly six rows so a long company list scrolls
 * inside the panel instead of running off the bottom of the screen.
 */
export const FilterOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  margin-top: 8px;

  /* Exclusive filters use a radio group, which is inline by default. */
  .ant-radio-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`

export const FilterEmptyText = styled.p`
  font-size: 14px;
  color: ${sureco.fg3};
  margin: 8px 0 4px;
`

/** Divides the option list from the Select all / Reset row beneath it. */
export const FilterPanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${sureco.border};
`

export const CompanyCell = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  max-width: 100%;

  /* The "+N more" trigger holds its width while the name truncates. */
  .ant-btn-link {
    flex-shrink: 0;
  }
`

/**
 * Truncates the company name rather than the cell, so the "+N more" affordance
 * survives a long name instead of being the first thing pushed out of view.
 */
export const CompanyName = styled.span`
  font-size: 14px;
  color: ${sureco.fg1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/**
 * Yes/No value in an access column. These columns are read as context, not as
 * something to act on, so a settled answer either way is plain text. Only the
 * in-between states carry a color, and the tooltip-bearing ones are underlined
 * so the extra detail is discoverable.
 */
const ACCESS_VALUE_COLORS: Record<
  RoleAccessTone,
  { fg: string; underline: boolean }
> = {
  granted: { fg: sureco.fg1, underline: false },
  pending: { fg: sureco.noticeText, underline: true },
  implied: { fg: sureco.fg3, underline: true },
  none: { fg: sureco.fg3, underline: false },
}

export const AccessValue = styled.span<{ $tone: RoleAccessTone }>`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ $tone }) => ACCESS_VALUE_COLORS[$tone].fg};
  ${({ $tone }) =>
    ACCESS_VALUE_COLORS[$tone].underline
      ? `border-bottom: 1px dotted currentColor; cursor: help;`
      : ''}
`

export const ClientPopoverList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
  min-width: 200px;
  max-width: 320px;
`

export const ClientPopoverItem = styled.span`
  font-size: 14px;
  color: ${sureco.fg1};
  padding: 2px 0;

  & + & {
    border-top: 1px solid ${sureco.borderSoft};
  }
`

export const HistoryMeta = styled.div`
  font-size: 12px;
  color: ${sureco.fg3};
  margin-top: 2px;
`

/* Holds the loading, error and empty states in the middle of the drawer, so a
   one-line message doesn't sit stranded under the header. */
export const HistoryPlaceholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const HistoryEventLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
`

export const HistoryEmailHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
  word-break: break-all;
  margin-bottom: 16px;
`

export const BulkBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  /* Below Ant modals/dropdowns (1000+), above table content. */
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 12px 20px;
  background: ${sureco.white};
  border: 1px solid ${sureco.border};
  border-radius: 12px;
  box-shadow:
    0 12px 24px -8px rgb(0 0 0 / 0.12),
    0 4px 8px -4px rgb(0 0 0 / 0.06);
  /* Centring with left: 50% leaves only half the viewport to shrink-fit into,
     which would wrap the actions onto a second row. max-content opts out of
     that, and the max-width still caps it on a narrow screen. */
  width: max-content;
  max-width: calc(100vw - 48px);
  flex-wrap: wrap;

  @media (max-width: 640px) {
    left: 16px;
    right: 16px;
    transform: none;
  }
`

export const BulkBarInfo = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const BulkBarCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
  line-height: 1.3;
`

/**
 * What each action would do to the selection, dotted apart. Kept on one line:
 * the separators sit before each stat, so wrapping would start a line with a
 * stray dot.
 */
export const BulkBarBreakdown = styled.span`
  display: block;
  font-size: 12px;
  color: ${sureco.fg2};
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const BulkBarStat = styled.span`
  /* Separators come from the layout, so no empty dot can be left behind when a
     count drops to zero. */
  & + &::before {
    content: '·';
    margin: 0 6px;
    color: ${sureco.fg3};
  }
`

export const BulkBarStatValue = styled.span`
  font-weight: 600;
  color: ${sureco.fg1};
`

export const BulkBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const BulkApplyAll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid ${sureco.border};
  border-radius: 6px;
  background: ${sureco.pageBg};
`

export const BulkApplyAllLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${sureco.fg2};
`

export const InviteModalForm = styled.div`
  .ant-form-item {
    margin-bottom: 20px;
  }
  .ant-form-item:last-child {
    margin-bottom: 0;
  }
  .ant-form-item-explain,
  .ant-form-item-extra {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.5;
  }
`

/* --- Access modals ------------------------------------------------------ */

export const ReasonField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 16px;
`

export const ReasonLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${sureco.fg1};
`

export const ReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  box-sizing: border-box;
  padding: 10px 12px;
  font: inherit;
  font-size: 14px;
  color: ${sureco.fg1};
  border: 1px solid ${sureco.borderStrong};
  border-radius: 6px;
  resize: vertical;

  &::placeholder {
    color: ${sureco.placeholder};
  }
  &:focus {
    outline: none;
    border-color: ${sureco.teal};
    box-shadow: 0 0 0 3px ${sureco.focusRing};
  }
`

export const WarningNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 18px;
  color: ${sureco.noticeText};
  background: ${sureco.noticeLight};
  border: 1px solid ${sureco.noticeBorder};
  border-radius: 6px;

  .anticon {
    flex: none;
    margin-top: 2px;
  }
`

/* --- Grant access modal ------------------------------------------------- */

export const GrantSection = styled.section`
  & + & {
    margin-top: 16px;
  }
`

export const GrantSectionHeader = styled.header`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-bottom: 6px;
`

export const GrantSectionTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${sureco.fg1};
`

export const GrantSectionHint = styled.span`
  font-size: 12px;
  color: ${sureco.fg3};
`

/** The role a grant confers, where there is only one and nothing to pick. */
export const GrantRoleValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${sureco.fg1};
`

/**
 * One scroll region for the whole confirm step. The sections used to scroll
 * individually, which stacked four scrollbars and still overran the viewport.
 */
export const GrantScroll = styled.div`
  max-height: min(46vh, 420px);
  overflow-y: auto;
  padding-right: 4px;
`

/** A count per action, so the mix is readable without scrolling the sections. */
export const GrantPlanSummary = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin: 0 0 12px;
  font-size: 12px;
  color: ${sureco.fg2};
`

export const GrantPlanItem = styled.span`
  &:not(:last-child)::after {
    content: '·';
    margin-left: 10px;
    color: ${sureco.fg3};
  }
`

export const GrantPlanCount = styled.strong`
  font-weight: 600;
  color: ${sureco.fg1};
`

export const GrantRowList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${sureco.border};
  border-radius: 6px;
`

export const GrantRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  min-width: 0;

  & + & {
    border-top: 1px solid ${sureco.borderSoft};
  }

  &:nth-child(even) {
    background: ${sureco.pageBg};
  }
`

export const GrantRowEmail = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${sureco.fg1};
  word-break: break-all;
`

export const GrantRowDetail = styled.span`
  font-size: 12px;
  color: ${sureco.fg3};
`

/**
 * Outcome label on a results row. Skipped reads as a notice rather than an
 * error: the operator did nothing wrong, there was simply nothing to do.
 */
export const GrantOutcome = styled.span<{
  $tone: 'success' | 'notice' | 'error'
}>`
  flex: none;
  align-self: flex-start;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  ${({ $tone }) => {
    if ($tone === 'success') {
      return `background: ${sureco.successLight}; color: ${sureco.successText}; border: 1px solid ${sureco.successBorder};`
    }
    if ($tone === 'notice') {
      return `background: ${sureco.noticeLight}; color: ${sureco.noticeText}; border: 1px solid ${sureco.noticeBorder};`
    }
    return `background: ${sureco.errorLight}; color: ${sureco.errorText}; border: 1px solid ${sureco.errorBorder};`
  }}
`

export const GrantSplitRow = styled(GrantRow)`
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

export const GrantRowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const GrantSummary = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: ${sureco.fg2};
`

export const GrantProgress = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  color: ${sureco.fg3};
`
